/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */


import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { htmlToPlainText } from "../../../utils/sanitize";
import Title from "../../ui/Title";
import Text from "../../ui/Text";
import Flex from "../../ui/Flex";
import Icon from "../../ui/Icon";
import { useAssetsSrc } from "../../../hooks/useAssetsSrc";

import useServiceSelection from "../../../hooks/useServiceSelection";
import usePluginUtils from "../../../hooks/usePluginUtils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/Tooltip";
import T from "../../ui/Translation";
import useGroupBookingCounter from "../../../hooks/useGroupBookingCounter";
import { GroupBookingCounter } from "../../GroupBookingCounter";
import { useBooking } from "../../../hooks/useBooking";
import {
  MembershipBadge,
  MetaGroup,
  ServiceDescriptionModal,
  ServiceDuration,
  ServiceFooter,
  ServiceItemButton,
  ServiceItemInfo,
  ServiceItemInfoHeader,
  ServiceItemInfoInner,
  ServiceItemInner,
  ServiceMedia,
  ServicePricing,
  StyledServiceItem,
} from "./ServiceItemParts";

const ServiceRecurringLabel = ({ recurring }) => {
  const tooltipContent = useMemo(() => {
    const { repeat_count, type, days } = recurring;

    return (
      <Text size="small">
        <T sessions={repeat_count}>%sessions% sessions</T>
        <br />
        {type === "fixed" && (
          <T type={type} days={days}>
            Repeat every %days% days
          </T>
        )}
        {type === "weekly" && <T type={type}>Repeat %type%</T>}
      </Text>
    );
  }, [recurring]);

  return (
    <Flex $align="center" $gap="0.25rem">
      <Icon.Renew color="iconStroke" />
      <Text color="dark">Recurring</Text>
      <Tooltip>
        <TooltipTrigger>
          <Icon.InfoCircle color="iconStroke" />
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </Flex>
  );
};

const ServiceItem = ({ service, category }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedServices, membershipAccessGrants, scrollToServiceId, clearScrollToServiceId } = useBooking();
  const { requestServiceSelection } = useServiceSelection(category);
  const { isPluginEnabled } = usePluginUtils();
  const serviceImage = useAssetsSrc(service.picture_preview);
  const { counter } = useGroupBookingCounter({ service, category });
  const itemRef = useRef(null);

  // A preselected service (e.g. the user returns from buying a membership that
  // unlocks it) can sit far down the list. When this item is that target, bring
  // it into view, then clear the one-shot flag so it's consumed exactly once —
  // manual selections never set it, so normal browsing never triggers a scroll.
  const isScrollTarget = scrollToServiceId != null && service.id === scrollToServiceId;
  useEffect(() => {
    if (!isScrollTarget || !itemRef.current) return;

    const timeoutId = setTimeout(() => {
      itemRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      clearScrollToServiceId();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isScrollTarget, clearScrollToServiceId]);

  const isSelected = selectedServices.some((s) => s.id === service.id);
  const isAvailable = service.is_available;
  const requiresMembership =
    isPluginEnabled("membership") && Array.isArray(service?.memberships) && service.memberships.length > 0;
  // Selected and a grant covers it: badge flips from "required" to "applied".
  const membershipApplied =
    requiresMembership && isSelected && membershipAccessGrants?.[service.id]?.clientMembershipId != null;
  const isRecurring = Boolean(service.recurring_settings);
  const hasDuration = Boolean(service.duration && service.duration > 0);

  const handleItemClick = useCallback(() => {
    if (isAvailable) {
      requestServiceSelection(service, isSelected);
    }
  }, [isAvailable, requestServiceSelection, service, isSelected]);

  const handleButtonClick = useCallback(() => {
    if (isAvailable) {
      requestServiceSelection(service, isSelected, isRecurring);
    }
  }, [requestServiceSelection, service, isSelected, isRecurring]);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const buttonIcon = useMemo(
    () => (isSelected ? <Icon.Check /> : <Icon.Plus />),
    [isSelected]
  );

  return (
    <>
      <StyledServiceItem
        ref={itemRef}
        selected={isSelected}
        disabled={!isAvailable}
        onClick={handleItemClick}
      >
        <ServiceMedia image={serviceImage} name={service.name} />

        <ServiceItemInner>
          <ServiceItemInfo $align="start" $justify="space-between" $gap="1rem">
            <ServiceItemInfoInner $gap="0.125rem" $align="start">
              <ServiceItemInfoHeader $gap="0.5rem" $align="center" $justify="space-between" $wrap="wrap">
                <Title size="h3">{service.name}</Title>
                {requiresMembership && <MembershipBadge applied={membershipApplied} />}
              </ServiceItemInfoHeader>
              {service.description && (
                <Text size="small" lineClamp={2} onReadMoreClick={openModal}>
                  {htmlToPlainText(service.description)}
                </Text>
              )}
            </ServiceItemInfoInner>

            <ServiceItemButton
              onClick={handleButtonClick}
              variant={isSelected ? "primary" : "outline"}
              size="icon"
              icon={buttonIcon}
              isActive={isSelected}
              tabIndex={isAvailable ? undefined : -1}
              aria-hidden={isAvailable ? undefined : true}
            />
          </ServiceItemInfo>

          <ServiceFooter $justify="space-between" $align="baseline" $gap="0.5rem">
            <ServicePricing service={service} isRecurring={isRecurring} />

            <MetaGroup $gap="0.25rem" $align="end">
              {isRecurring && hasDuration && (
                <ServiceDuration service={service} isRecurring={isRecurring} />
              )}
              {isRecurring && (
                <ServiceRecurringLabel recurring={service.recurring_settings} />
              )}
              {counter && <GroupBookingCounter service={service} category={category} />}
            </MetaGroup>
          </ServiceFooter>
        </ServiceItemInner>
      </StyledServiceItem>

      <ServiceDescriptionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        service={service}
        image={serviceImage}
      />
    </>
  );
};

export default ServiceItem;
